import type { Percent, ReadonlyVec2 } from "@core/types";
import { asPercent } from "@core/types";
import type { Collectible } from "@entities/collectible";
import {
  FLOWER_ENERGY_BONUS,
  FLOWER_USE_RADIUS,
  GLOWFLY_COLLECT_RADIUS,
  GLOWFLY_ENERGY_BONUS,
  SAND_COLLECT_RADIUS,
  SAND_ENERGY_BONUS,
  SEED_COLLECT_RADIUS,
  SEED_REVEAL_RADIUS,
} from "@data/balance";

/** Эффект, возвращаемый checkCollections за один тик. */
export type CollectionEffect =
  | { kind: "seed-revealed"; id: string }
  | { kind: "seed-collected"; id: string }
  | { kind: "sand-collected"; id: string; energyDelta: Percent }
  | { kind: "flower-used"; id: string; energyDelta: Percent }
  | { kind: "glowfly-collected"; id: string; energyDelta: Percent };

const distance = (a: ReadonlyVec2, b: ReadonlyVec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Чистая функция: возвращает массив эффектов сбора и мутирует
 * флаги collected/used на collectibles. Эмиттер событий — снаружи.
 */
export const checkCollections = (
  player: ReadonlyVec2,
  glow: boolean,
  items: readonly Collectible[],
): CollectionEffect[] => {
  const effects: CollectionEffect[] = [];
  for (const item of items) {
    const d = distance(player, item.position);

    if (item.type === "seed" && !item.collected) {
      const visible = glow && d < SEED_REVEAL_RADIUS;
      if (visible) effects.push({ kind: "seed-revealed", id: item.id });
      if (visible && d < SEED_COLLECT_RADIUS) {
        item.collected = true;
        effects.push({ kind: "seed-collected", id: item.id });
      }
    } else if (item.type === "sand" && !item.collected && d < SAND_COLLECT_RADIUS) {
      item.collected = true;
      effects.push({
        kind: "sand-collected",
        id: item.id,
        energyDelta: asPercent(SAND_ENERGY_BONUS),
      });
    } else if (item.type === "flower" && !item.used && d < FLOWER_USE_RADIUS) {
      item.used = true;
      effects.push({
        kind: "flower-used",
        id: item.id,
        energyDelta: asPercent(FLOWER_ENERGY_BONUS),
      });
    } else if (item.type === "glowfly" && !item.collected && d < GLOWFLY_COLLECT_RADIUS) {
      item.collected = true;
      effects.push({
        kind: "glowfly-collected",
        id: item.id,
        energyDelta: asPercent(GLOWFLY_ENERGY_BONUS),
      });
    }
  }
  return effects;
};
